/**
 * Homestead claim rules — the pure, server-authoritative logic deciding whether a
 * beacon may be planted, and the budget accounting for pieces/edits. Kept pure and
 * pmath-safe (no trig) so the server owns the decision and tests need no sampler.
 *
 * The town-overlap input is INJECTED (a precomputed SettlementInfluence) so this
 * core is trivially testable; `claimTownInfluence` is the thin sampler-coupled
 * wrapper the server calls to produce it. Design:
 * docs/superpowers/specs/2026-07-17-homesteads-design.md §1.
 */
import type { PlanetSampler } from "./../universe.js";
import { settlementInfluenceAt, type SettlementInfluence } from "./../worldgen/settlements.js";
import { tierDef, type Beacon, type Vec3 } from "./types.js";

/** Public-galaxy caps (home:<uid> planets are uncapped). Tunable per the spec. */
export const PUBLIC_MAX_TIER = 1;
export const PUBLIC_MAX_BEACONS_PER_PLANET = 2;

/** The overlap/count-relevant subset of a beacon (full Beacon is assignable). */
export interface BeaconLite {
  ownerUid: string;
  anchor: Vec3;
  radius: number;
}

export interface ClaimRequest {
  /** Is this a private home:<uid> planet? (uncaps tier + count.) */
  isHome: boolean;
  ownerUid: string;
  anchor: Vec3;
  /** Desired tier; the claim radius is derived from it (single source of truth). */
  tier: number;
  /** Every beacon already on this planet (any owner). */
  existing: readonly BeaconLite[];
  /** Procedural-settlement footprint at the anchor, or null (see claimTownInfluence). */
  townInfluence: SettlementInfluence | null;
}

export type ClaimDenyReason = "tier-too-high" | "too-many-beacons" | "overlap" | "on-town";
export type ClaimVerdict = { ok: true } | { ok: false; reason: ClaimDenyReason };

function dist2(a: Vec3, b: Vec3): number {
  const dx = a[0] - b[0], dy = a[1] - b[1], dz = a[2] - b[2];
  return dx * dx + dy * dy + dz * dz;
}

/** Exact sphere-vs-sphere overlap of two claims (strict: exactly touching is allowed). */
export function beaconsOverlap(a: BeaconLite, b: BeaconLite): boolean {
  const rr = a.radius + b.radius;
  return dist2(a.anchor, b.anchor) < rr * rr;
}

/**
 * May this claim be planted? Order matters only for which reason surfaces first.
 * Overlap is rejected against OTHER players' claims only — a player may stack/adjoin
 * their OWN beacons (that is how a whole home planet is covered, per the spec).
 */
export function canClaim(req: ClaimRequest): ClaimVerdict {
  const radius = tierDef(req.tier).radius;

  if (!req.isHome && req.tier > PUBLIC_MAX_TIER) return { ok: false, reason: "tier-too-high" };

  if (!req.isHome) {
    let mine = 0;
    for (const e of req.existing) if (e.ownerUid === req.ownerUid) mine++;
    if (mine >= PUBLIC_MAX_BEACONS_PER_PLANET) return { ok: false, reason: "too-many-beacons" };
  }

  const self: BeaconLite = { ownerUid: req.ownerUid, anchor: req.anchor, radius };
  for (const e of req.existing) {
    if (e.ownerUid === req.ownerUid) continue; // own claims may adjoin/overlap
    if (beaconsOverlap(self, e)) return { ok: false, reason: "overlap" };
  }

  const ti = req.townInfluence;
  if (ti && ti.dist <= ti.radius + radius) return { ok: false, reason: "on-town" };

  return { ok: true };
}

/**
 * The procedural-settlement footprint near a candidate anchor, or null if clear.
 * Thin sampler-coupled wrapper feeding `canClaim.townInfluence`; queries with the
 * claim radius as slack so a town within reach is returned. `anchor` is planet-local
 * metres (settlementInfluenceAt normalizes it to a direction internally).
 */
export function claimTownInfluence(sampler: PlanetSampler, anchor: Vec3, radius: number): SettlementInfluence | null {
  return settlementInfluenceAt(sampler, anchor[0], anchor[1], anchor[2], radius);
}

// --- Budget accounting (the scalability cap: tier gates both) ---

/** Would a build of `pieceCount` pieces + `editCells` edit-raster cells fit the tier? */
export function withinBudgets(b: Beacon, pieceCount: number, editCells: number): boolean {
  const t = tierDef(b.tier);
  return pieceCount <= t.pieceBudget && editCells <= t.editBudget;
}

/** Can one more piece be placed given the current count? */
export function canPlacePiece(b: Beacon, currentPieces: number): boolean {
  return currentPieces < tierDef(b.tier).pieceBudget;
}
